import { Button } from '@equitymultiple/react-eui';
import { graphql, HeadFC, Link, PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { Seo } from 'gatsby-plugin-wpgraphql-seo';
import React, { useEffect } from 'react';

import Accordion from '../../components/Accordion/Accordion';
import ExpandedKeg from '../../components/ExpandedKeg/ExpandedKeg';
import GetStarted from '../../components/GetStarted/GetStarted';
import Layout from '../../components/layout';
import { SharedHead } from '../../components/SharedHead/SharedHead';
import { GetStartedPages, Routes } from '../../constants';
import useUserData from '../../hooks/useUserData';
import { externalContactLink } from '../../utils/constants';
import * as styles from './Investment.module.scss';

const Investment: React.FC<PageProps<Queries.InvestmentPageQuery>> = ({
  data,
}) => {
  const { userData } = useUserData();
  const { investmentPage } = data.post
    ?.template as Queries.InvestmentTemplateFragment;

  useEffect(() => {
    const hash = location.hash.split('?')[0].split('/')[0].replace('#', '');

    const anchor = document.getElementById(hash);
    if (anchor) {
      window.scrollTo({ top: 0 });
      setTimeout(() => {
        window.scrollTo({
          top: (anchor?.getBoundingClientRect().y as number) - 50,
          behavior: 'smooth',
        });
      }, 500);
    }
  });
  return (
    <Layout>
      <Seo post={data.post} />
      <div>
        <div className="container">
          <div className="row justify-content-center align-items-center">
            <div
              className={`col-xl-6 order-2 order-xl-1 ${styles.heroImageContainer}`}
            >
              <div className={styles.heroCopy}>
                <h2 className="heading2 expressive">
                  An Elevated Path to Building Wealth
                </h2>
                <p className="subHeading">
                  EquityMultiple’s complementary investment strategies form a
                  unique wealth generation ecosystem, tailored to your goals.
                </p>
                {userData.name ? (
                  <Button target="_blank" href={Routes.viewInvestments.url}>
                    {Routes.viewInvestments.name}
                  </Button>
                ) : (
                  <Button
                    target="_blank"
                    href={Routes.signUp.url}
                    variant="orange"
                  >
                    Get Started
                  </Button>
                )}
              </div>
            </div>
            <div
              className={`col-xl-6 order-1 order-xl-2 ${styles.heroImageContainer}`}
            >
              <StaticImage
                className={styles.heroImage}
                src="../../assets/illustrations/investment-hero-illustration.png"
                alt="Roads leading to a city on the horizon with a setting sun."
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.investmentEcosystemSection}>
        <div className="container">
          <div className="row">
            <div className="col">
              <h3 className="heading3">Investment Ecosystem</h3>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.hexOverlappingContainer}>
        <StaticImage
          className={styles.investmentHeaderImage}
          src="../../assets/illustrations/sparkle-1.png"
          alt="Floating objects that cumulatively look like a sparkle"
        />
      </div>

      <ExpandedKeg investmentTemplateData={investmentPage} />
      <GetStarted variant={GetStartedPages.Investment} />
      <div className="container">
        <div className={styles.investmentFaqAccordion}>
          <div>
            <h3 className={`${styles.investmentFaqHeader} heading3`}>FAQs</h3>
          </div>

          <Accordion summary="What is EquityMultiple and why should I use it to invest in real estate?">
            <br />

            <p className="body1">
              EquityMultiple is a commercial real estate investment and
              technology firm that provides accredited investors access to
              professionally managed, private real estate transactions across
              property types and risk profiles. EquityMultiple’s mission is to
              make real estate investing simple, accessible, and transparent. To
              date, EquityMultiple’s investors have participated in over $3
              billion in commercial real estate transactions through its online
              investing platform. The firm pairs innovative technology with real
              estate experience and industry-leading investor services to offer
              an unparalleled investing experience.
            </p>
            <br />
            <br />
            <p className="body1">
              We help self-directed investors build a more diversified
              portfolio, offering rigorously vetted,{' '}
              <Link to="/blog/passive-real-estate-investing-explained">
                passive real estate
              </Link>{' '}
              investments for as little as $5,000.
            </p>
            <br />
            <br />
            <p className="body1">
              <Link to={Routes.ourProcess.url}>{Routes.ourProcess.name}</Link>:
              We offer robust in-house underwriting and a seamless investing
              process, which allow accredited investors to participate in direct
              real estate transactions and real estate funds alongside
              experienced sponsors and lenders. Our offerings span a variety of
              markets, asset types, and return profiles so you can meet your
              investment goals.
            </p>
            <br />
            <br />
            <p className="body1">
              For more on the landscape of modern real estate investing, and
              where EquityMultiple fits in, please consult{' '}
              <Link to="/blog/cre-basics-intro-modern-real-estate-investing">
                our learning series
              </Link>
              . More information regarding the historical performance of
              EquityMultiple investments can be found on our{' '}
              <Link to="/track-record">Track Record</Link> page.
            </p>
            <br />
            <br />
          </Accordion>
          <Accordion summary="I would like to speak with someone at EquityMultiple. How can I get in touch?">
            <br />
            <p className="body1">
              We pride ourselves on our industry-leading customer service. You
              can reach support on any page of www.equitymultiple.com by
              clicking the blue circle in the lower right-hand corner of your
              screen. For general support you can also reach us by email at
              help@equitymultiple.com or by phone at 646-692-0062. If you have
              any feedback on how to improve EquityMultiple, please let us know
              at{' '}
              <a href="mailto:ir@equitymultiple.com">ir@equitymultiple.com</a>{' '}
              or schedule a call with Investor Relations{' '}
              <a target="_blank" href={externalContactLink} rel="noreferrer">
                here
              </a>
              . No matter how you reach out to us, we’ll make sure that your
              question gets answered.
            </p>
            <br />
            <br />
          </Accordion>
          <Accordion summary="Will my investment be safe with EquityMultiple?">
            <br />
            <p className="body1">
              <Link to="/about">Our Investments Team</Link> has been involved in
              billions of dollars in real estate transactions. We work
              tirelessly to identify and underwrite investments that deliver
              compelling potential risk-adjusted returns. We structure
              investments to offer the maximum degree of investor protections,
              and our Asset Management Team works throughout the lifecycle of
              each investment to protect principal and maximize your returns.
            </p>
            <br />
            <br />
            <p className="body1">
              After investing with us, we will continue to oversee your
              investment until exit. Our Asset Management Team and the rest of
              the EquityMultiple Team will continue to monitor the investments
              and send asset management reports periodically to communicate
              progress.
            </p>
            <br />
            <br />
            <p className="body1">
              Of course, keeping your information secure is our top priority. We
              host and manage our platform’s physical infrastructure within
              Amazon’s secure data centers and use bank-grade protocols to
              transmit and store your data, and maintain a development policy
              which puts your privacy first.
            </p>
            <br />
            <br />
            <p className="body1">
              We’re also committed to transparency and customer service – we’re
              always happy to field questions, and discuss any concerns over the
              phone.
            </p>
            <br />
            <br />
          </Accordion>
          <Accordion summary="How does EquityMultiple compare to other real estate investing platforms?">
            <br />
            <p className="body1">
              <span className="emphasis">Diverse offerings:</span> We are one of
              the few platforms that offers equity, preferred equity, and senior
              debt investments, letting investors select the type(s) of assets
              that fit their investment goals. We generally focus on the
              mid-market, commercial real estate space, with investments
              reflecting a diverse array of multi-tenant properties. Investments
              on our platform are generally focused on strong cash flow and all
              payments and updates are centrally managed and administered by
              EquityMultiple.
            </p>
            <br />
            <br />
            <p className="body1">
              <span className="emphasis">Experienced team:</span> We offer a
              unique approach and a management team with far-reaching industry
              experience.
            </p>
            <br />
            <br />
            <p className="body1">
              <span className="emphasis">
                Industry-leading customer service:
              </span>
              We are committed to transparency, attentive customer service, and
              thorough asset management updates throughout the life of
              investments. For more on how we’re different, please see{' '}
              <Link to="/blog/how-were-different">this article</Link>.
            </p>
            <br />
            <br />
          </Accordion>
          <Button className={styles.toFaqButton} href="/faq" variant="outlined">
            All FAQs
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export const Head: HeadFC<Queries.InvestmentPageQuery> = ({
  location,
  data,
}) => {
  return <SharedHead data={data} location={location} />;
};

export default Investment;

export const pageQuery = graphql`
  fragment InvestmentTemplate on WpTemplate_Investment {
    investmentPage {
      earnImage {
        gatsbyImage(width: 550)
        altText
      }
      growImage {
        gatsbyImage(width: 550)
        altText
      }
      keepImage {
        gatsbyImage(width: 550)
        altText
      }
      growInvestmentMetric1Label
      growInvestmentMetric1Value
      growInvestmentMetric2Label
      growInvestmentMetric2Value
      growInvestmentMetric3Label
      growInvestmentMetric3Value
      earnInvestmentMetric1Label
      earnInvestmentMetric1Value
      earnInvestmentMetric2Label
      earnInvestmentMetric2Value
      earnInvestmentMetric3Label
      earnInvestmentMetric3Value
      keepInvestmentMetric1Label
      keepInvestmentMetric1Value
      keepInvestmentMetric2Label
      keepInvestmentMetric2Value
      keepInvestmentMetric3Label
      keepInvestmentMetric3Value
      growInvestmentTitle
      growLink
      keepInvestmentTitle
      keepLink
      earnInvestmentTitle
      earnLink
      faqs {
        faq {
          ... on WpFAQ {
            title
            content
          }
        }
      }
    }
  }
  query InvestmentPage(
    # these variables are passed in via createPage.pageContext in gatsby-node.js
    $id: String!
  ) {
    # selecting the current post by id
    post: wpPage(id: { eq: $id }) {
      seo {
        ...SeoFragment
      }
      ldJsonSchema {
        multipleSchemas {
          schemaStringContent
        }
        schemaStringContent
      }
      id
      title
      uri
      content
      template {
        ...InvestmentTemplate
      }
    }
  }
`;
